/*
  Impact counter (task 4.1): count-up for <impact-counter-value> in
  snippets/impact-counter-content.liquid. Loaded by sections/impact-counter.liquid only
  when "Animate number" is on.

  - The final, formatted number is already in the HTML (Liquid). This script never
    changes it. If the number is already on screen when the script runs, it is left as
    it is (no jump back to 0). Otherwise, while it is still off screen, a counting copy
    showing 0 is laid on top; the copy counts up once about half of the number is in
    view (IntersectionObserver), then it is removed.
  - Runs once per counter. Does nothing for prefers-reduced-motion, without
    IntersectionObserver, or if anything goes wrong (the final number then stays as is).
  - The final number keeps its space while counting (tabular figures plus the hidden
    final number holding the box), so nothing around it moves.
  - Wrapped in a function so it adds no globals; safe to load more than once.
*/
(function () {
  'use strict';

  const ELEMENT_NAME = 'impact-counter-value';

  if (!window.customElements || window.customElements.get(ELEMENT_NAME)) return;

  const DURATION = 2000;
  // Observer reports at 0 (enters or leaves the screen) and at half in view. The count
  // starts at 0.45 rather than 0.5 to allow for sub-pixel rounding of the ratio.
  const THRESHOLDS = [0, 0.5];
  const START_RATIO = 0.45;
  const COUNTING_CLASS = 'impact-counter__value--counting';
  const LIVE_CLASS = 'impact-counter__value-text--live';
  const TEXT_SELECTOR = '.impact-counter__value-text';
  const DIGITS_SELECTOR = '[data-impact-counter-digits]';

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function easeOutCubic(progress) {
    return 1 - Math.pow(1 - progress, 3);
  }

  class ImpactCounterValue extends HTMLElement {
    connectedCallback() {
      if (this.counterState) return;

      try {
        this.setUp();
      } catch (error) {
        this.finish();
      }
    }

    disconnectedCallback() {
      // Removed before it finished (for example a theme editor re-render): restore the final number.
      if (this.counterState !== 'done') this.tearDown(null);
    }

    setUp() {
      if (prefersReducedMotion()) return;
      if (!('IntersectionObserver' in window) || typeof window.requestAnimationFrame !== 'function') return;

      const target = parseInt(this.getAttribute('data-value'), 10);
      if (!(target > 0)) return;

      const finalText = this.querySelector(TEXT_SELECTOR);
      if (!finalText || !finalText.querySelector(DIGITS_SELECTOR)) return;

      // Nothing changes on screen yet: the observer's first report says whether the
      // number is already visible (it always sends one right after observe()).
      this.targetValue = target;
      this.finalText = finalText;
      this.counterState = 'checking';

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: THRESHOLDS });
      this.observer.observe(this);
    }

    onIntersect(entries) {
      const entry = entries[entries.length - 1];
      if (!entry) return;

      try {
        if (this.counterState === 'checking') {
          // Already on screen: keep the final number rather than resetting it to 0.
          if (entry.isIntersecting) {
            this.finish();
          } else {
            this.arm();
          }
          return;
        }

        if (this.counterState === 'waiting' && entry.isIntersecting && entry.intersectionRatio >= START_RATIO) {
          this.start();
        }
      } catch (error) {
        this.finish();
      }
    }

    arm() {
      // Still off screen: lay the counting copy, showing 0, over the final number.
      const live = this.finalText.cloneNode(true);
      live.classList.add(LIVE_CLASS);
      this.liveDigits = live.querySelector(DIGITS_SELECTOR);
      this.liveDigits.textContent = this.format(0);

      this.live = live;
      this.appendChild(live);
      this.classList.add(COUNTING_CLASS);
      this.counterState = 'waiting';
    }

    start() {
      if (this.counterState !== 'waiting') return;
      this.stopObserving();

      if (prefersReducedMotion()) {
        this.finish();
        return;
      }

      this.counterState = 'running';
      let startTime = null;

      const step = (now) => {
        try {
          if (startTime === null) startTime = now;
          const progress = Math.min((now - startTime) / DURATION, 1);

          if (progress >= 1) {
            this.finish();
            return;
          }

          this.liveDigits.textContent = this.format(Math.floor(this.targetValue * easeOutCubic(progress)));
          this.frame = window.requestAnimationFrame(step);
        } catch (error) {
          this.finish();
        }
      };

      this.frame = window.requestAnimationFrame(step);
    }

    finish() {
      this.tearDown('done');
    }

    tearDown(nextState) {
      this.stopObserving();

      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      if (this.live && this.live.parentNode) this.live.parentNode.removeChild(this.live);
      this.live = null;
      this.liveDigits = null;
      this.finalText = null;
      this.classList.remove(COUNTING_CLASS);
      this.counterState = nextState;
    }

    stopObserving() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    format(value) {
      const separator = this.hasAttribute('data-separator') ? this.getAttribute('data-separator') : ',';
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    }
  }

  window.customElements.define(ELEMENT_NAME, ImpactCounterValue);
})();
